import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslator } from '@picsart/rc/services/localization';
import View from '@picsart/view/View';
import { Spacings, Typography } from '@picsart/cascade';
import Text from '@picsart/text/Text';
import Select from '@picsart/select/Select';
import { SelectSizesMD } from '@picsart/select';
import SelectItem from '@picsart/select/SelectItem';
import Dynamic from '@picsart/editorlayout/Dynamic';
import DynamicHeader from '@picsart/editorlayout/DynamicHeader';
import DynamicBody from '@picsart/editorlayout/DynamicBody';

import {
  BATCH_TOOLS,
  RESIZE_SIDE_ENUM,
  resizeBottomSheetSize,
  resizeBySideParams,
  resizeDefaultSizes,
  TRANSPARENT_OPTION,
} from 'pages/Batch/configs';
import { DataActionTypes } from 'pages/Batch/sections/Context';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import analytics, { SubToolNames } from 'pages/Batch/utils/analytics';
import useMount from 'hooks/common/useMount';
import useHeaderStyles from 'pages/Batch/styles';
import dynamicTestId from 'utils/common/dynamicTestId';

import InputSizes from './InputSizes';
import ResizeBySocial from './ResizeBySocial';
import ResizeByPadding from './ResizeByPadding';

const ResizeTool = () => {
  const headerClasses = useHeaderStyles();
  const translate = useTranslator();
  const { dispatchData, resizeParams, setBottomSheetCustomHeight } = useBatchData();

  const [currentSide, setCurrentSide] = useState<RESIZE_SIDE_ENUM>(RESIZE_SIDE_ENUM.socials);
  const [inputValue, setInputValue] = useState(resizeDefaultSizes);

  const handleActivePadding = useCallback((isActive: boolean) => {
    dispatchData({
      type: DataActionTypes.SET_RESIZE_PARAMS,
      params: {
        ...resizeParams,
        width: inputValue[0],
        height: inputValue[1],
        usePadding: isActive,
      },
    });
  }, [inputValue, dispatchData, resizeParams]);

  const handleChangeSide = useCallback(side => {
    setCurrentSide(side);
    setBottomSheetCustomHeight(resizeBottomSheetSize[side as RESIZE_SIDE_ENUM]);
    analytics.utils().setSubTool(BATCH_TOOLS.resize, {
      name: SubToolNames.resizeBy,
      value: side,
    });
    if (side === RESIZE_SIDE_ENUM.socials) {
      return;
    }
    dispatchData({
      type: DataActionTypes.SET_RESIZE_PARAMS,
      params: {
        ...resizeParams,
        width: side !== RESIZE_SIDE_ENUM.height ? inputValue[0] : undefined,
        height: side !== RESIZE_SIDE_ENUM.width ? inputValue[1] : undefined,
        usePadding: side === RESIZE_SIDE_ENUM.exactSize,
        isOriginal: false,
      },
    });
  }, [dispatchData, inputValue, resizeParams, setBottomSheetCustomHeight]);

  useEffect(() => {
    if ([RESIZE_SIDE_ENUM.socials, RESIZE_SIDE_ENUM.exactSize].includes(currentSide)) {
      analytics.utils().setSubTool(BATCH_TOOLS.resize, {
        name: SubToolNames.usePadding,
        value: true,
      });
      analytics.utils().setSubTool(BATCH_TOOLS.resize, {
        name: SubToolNames.paddingColor,
        value: TRANSPARENT_OPTION,
      });
    }
  }, [currentSide]);

  useMount(() => {
    handleChangeSide(RESIZE_SIDE_ENUM.socials);
    analytics.openEvent(BATCH_TOOLS.resize);
  });

  return (
    <Dynamic
      header={(
        <DynamicHeader
          className={headerClasses.customHeader}
          title={translate('Resize')}
        >
          <View flexDirection="column" gap={Spacings.s8}>
            <Text size={Typography.t4}>{translate('Resize by')}</Text>
            <Select
              label={translate('Resize by')}
              onChange={handleChangeSide}
              value={currentSide}
              size={SelectSizesMD}
            >
              {resizeBySideParams.map(item => (
                <SelectItem dataTestId={dynamicTestId('resize-by', item)} key={item} value={item}>{item}</SelectItem>
              ))}
            </Select>
          </View>
        </DynamicHeader>
      )}
      body={(
        <DynamicBody>
          <View marginBlockEnd={Spacings.s16} flexDirection="column">
            {currentSide === RESIZE_SIDE_ENUM.socials ? (
              <ResizeBySocial />
            ) : (
              <InputSizes
                onChangeValue={setInputValue}
                activeSide={currentSide}
                values={inputValue}
              />
            )}
            {currentSide === RESIZE_SIDE_ENUM.exactSize && (
            <ResizeByPadding onActivePadding={handleActivePadding} />
            )}
          </View>
        </DynamicBody>
      )}
    />
  );
};

export default memo(ResizeTool);
